import { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SoccerBallIcon, ListChecksIcon, ClockIcon } from '@phosphor-icons/react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IFieldWorkout } from '../../../models/field-programming';
import styles from './AthleteFieldWorkoutsSection.module.scss';

interface Props {
  athleteId: string;
}

export const AthleteFieldWorkoutsSection: FC<Props> = ({ athleteId }) => {
  const api = useEndpoints();

  // Lista template scheda Campo della libreria. Il backend espone solo la
  // libreria globale: il filtro per atleta arriverà quando ci sarà l'endpoint
  // dedicato. Per ora la usiamo come lista di "schede recenti" del coach.
  const workoutsQ = useQuery({
    queryKey: ['athleteFieldWorkouts', athleteId],
    queryFn: () =>
      api.fieldProgramming.workouts.list({ limit: 50 }).then((r) => r.data.docs),
    staleTime: 60 * 1000,
  });

  const workouts: IFieldWorkout[] = workoutsQ.data ?? [];

  if (workoutsQ.isLoading) {
    return <div className={styles.loading}>Caricamento schede campo...</div>;
  }

  if (workouts.length === 0) {
    return (
      <div className={styles.empty}>
        <SoccerBallIcon size={36} weight="duotone" className={styles.emptyIcon} />
        <p className={styles.emptyTitle}>Nessuna scheda Campo</p>
        <p className={styles.emptyHint}>
          Usa il pulsante <strong>Nuova scheda Campo</strong> per creare un
          allenamento individuale assegnato direttamente a questo atleta.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.list}>
      {workouts.map((w) => {
        const blockCount = w.blocks?.length ?? 0;
        const exerciseCount = (w.blocks ?? []).reduce(
          (s, b) => s + (b.exercises?.length ?? 0),
          0,
        );
        const totalDuration = (w.blocks ?? []).reduce(
          (s, b) => s + (b.durationMinutes ?? 0),
          0,
        );

        return (
          <div key={w._id} className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.cardIcon}>
                <SoccerBallIcon size={18} weight="bold" />
              </div>
              <div className={styles.cardTitleBlock}>
                <p className={styles.cardTitle}>{w.name}</p>
                {w.description && <p className={styles.cardDesc}>{w.description}</p>}
              </div>
              {w.sport && <span className={styles.sportTag}>{w.sport}</span>}
            </div>

            <div className={styles.cardStats}>
              <span className={styles.stat}>
                <ListChecksIcon size={12} weight="bold" />
                {blockCount} blocch{blockCount === 1 ? 'o' : 'i'}
              </span>
              <span className={styles.stat}>
                <ListChecksIcon size={12} weight="bold" />
                {exerciseCount} esercizi
              </span>
              {totalDuration > 0 && (
                <span className={styles.stat}>
                  <ClockIcon size={12} weight="bold" />
                  {totalDuration}'
                </span>
              )}
            </div>

            {w.tags && w.tags.length > 0 && (
              <div className={styles.tagsRow}>
                {w.tags.slice(0, 6).map((t) => (
                  <span key={t} className={styles.tag}>
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AthleteFieldWorkoutsSection;
